import { useMemo } from 'react';

import { EMPTY_VALUE, Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Icon, Separator, Text, ToggleTip } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { isNil } from 'es-toolkit/compat';

import { ReturnDetailsDocument } from './graphql/ReturnDetails.generated';

import { trackClick } from '../../../../features/track';
import { CashAccount, DateRangeOption } from '../../../../graphql/schema';
import { formatReturn, formatReturnColor } from '../../../shared/utils';
import { UnderstandingReturnsDialog } from '../../../user/components/accounts-card/components/understanding-returns/UnderstandingReturnsDialog';

export type ReturnsCardProps = {
  accountId: string;
  dateRangeOption: DateRangeOption;
};

const formatAPY = (value: string | null) => (value && parseFloat(value) > 0 ? `${value}%` : EMPTY_VALUE);

export const ReturnDetailsCard: React.FC<ReturnsCardProps> = ({
  accountId,
  dateRangeOption = DateRangeOption.AllTime,
}) => {
  // Memoize array to prevent repeating queries on array initializtions
  const trackedIds = useMemo(() => [accountId], [accountId]);

  const handleInterestPendingToolTipOpen = (open: boolean) => {
    if (open) {
      trackClick({
        name: 'interestPendingToolTip',
        module: 'Performance',
        location: 'ReturnDetailsCard',
      });
    }
  };

  const { data } = useGraphQLQuery(ReturnDetailsDocument, {
    variables: { accountIds: trackedIds, dateRangeOption },
    throwOnError: true,
  });

  if (!data) {
    return <CardLoader />;
  }

  const { totals, accounts } = data.returnDetails;
  const { timeWeightedRateOfReturn, moneyWeightedRateOfReturn, interest } = totals;

  const timeWeightedReturn = timeWeightedRateOfReturn?.cumulative;
  const annualizedTimeWeightedReturn = timeWeightedRateOfReturn?.annualized;
  const moneyWeightedReturn = moneyWeightedRateOfReturn?.cumulative;
  const annualizedMoneyWeightedReturn = moneyWeightedRateOfReturn?.annualized;

  const isCashAccount = accounts?.[0]?.__typename === 'CashAccount';
  const apy = isCashAccount ? (accounts?.[0] as CashAccount).apy?.rate : null;
  const interestAccrued = Money(interest?.accrued || 0);

  return (
    <Card
      css={{
        display: 'flex',
        gap: 'space.300',
        padding: 'space.300',
        minWidth: '373px',
      }}
    >
      <Card.Header>
        <H2 variant="title200">Account returns</H2>
      </Card.Header>
      <Card.Content css={{ display: 'grid' }}>
        <Separator />
        {!isCashAccount && (
          <>
            <Box css={{ display: 'grid', gap: 'space.100', marginTop: 'space.300' }}>
              <section aria-label="Time-weighted return details">
                <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text role="heading" variant="text100">
                    Time-weighted return
                  </Text>
                  <Text variant="text100" css={{ color: formatReturnColor(timeWeightedReturn) }}>
                    {formatReturn(timeWeightedReturn)}
                  </Text>
                </Box>
                {!isNil(annualizedTimeWeightedReturn) && (
                  <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text role="heading" variant="text50" css={{ color: 'text.weak' }}>
                      Annualized
                    </Text>
                    <Text variant="text50" css={{ color: formatReturnColor(annualizedTimeWeightedReturn) }}>
                      {formatReturn(annualizedTimeWeightedReturn)}
                    </Text>
                  </Box>
                )}
              </section>
            </Box>

            <Box css={{ display: 'grid', gap: 'space.100', marginTop: 'space.200' }}>
              <section aria-label="Money-weighted return details">
                <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text role="heading" variant="text100">
                    Money-weighted return
                  </Text>
                  <Text variant="text100" css={{ color: formatReturnColor(moneyWeightedReturn) }}>
                    {formatReturn(moneyWeightedReturn)}
                  </Text>
                </Box>
                {!isNil(annualizedMoneyWeightedReturn) && (
                  <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text role="heading" variant="text50" css={{ color: 'text.weak' }}>
                      Annualized
                    </Text>
                    <Text variant="text50" css={{ color: formatReturnColor(annualizedMoneyWeightedReturn) }}>
                      {formatReturn(annualizedMoneyWeightedReturn)}
                    </Text>
                  </Box>
                )}
              </section>
            </Box>
            <Separator css={{ marginTop: 'space.300' }} />
          </>
        )}
        {isCashAccount && (
          <>
            <Box css={{ display: 'grid', gap: 'space.200' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text variant="text100">Current APY</Text>
                <Text variant="text100" css={{ color: formatReturnColor(parseFloat(apy || '0')) }}>
                  {formatAPY(apy)}
                </Text>
              </Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
                  <Text variant="text100">Interest to be paid</Text>
                  {interest?.accruedExplanation && (
                    <ToggleTip onOpenChange={handleInterestPendingToolTipOpen}>
                      <ToggleTip.Trigger iconName="InfoCircle" label="Accrued Interest Explanation" />
                      <ToggleTip.Content css={{ maxWidth: '300px' }}>{interest.accruedExplanation}</ToggleTip.Content>
                    </ToggleTip>
                  )}
                </Box>
                <Text variant="text100" css={{ color: formatReturnColor(interestAccrued.cents) }}>
                  {formatMoney(interestAccrued)}
                </Text>
              </Box>
            </Box>
          </>
        )}
      </Card.Content>
      {!isCashAccount && (
        <Card.Footer>
          <UnderstandingReturnsDialog>
            <>
              <Text as="span">Understanding your returns</Text>
              <Icon name="InfoCircle" size="xsmall" />
            </>
          </UnderstandingReturnsDialog>
        </Card.Footer>
      )}
    </Card>
  );
};
